import type { AppUser } from '@/lib/auth';
import { requireAdminOrManager } from '@/lib/auth/guards';
import {
  trackPropertyCreateFailure,
  trackPropertyCreateSuccess,
  trackPropertyUpdateFailure,
  trackPropertyUpdateSuccess,
} from '@/lib/analytics/events';
import { normalizeError, fromZodError, isServiceError } from '@/lib/services/errors';
import {
  createProperty,
  deleteProperty,
  getPropertyById,
  listProperties,
  updateProperty,
} from '@/lib/supabase/properties';
import type {
  PaginationInput,
  PropertyDetailResult,
  PropertyFormValues,
  PropertyListFilters,
  PropertyListResult,
  PropertyListSorting,
} from '@/lib/types/property';
import {
  paginationSchema,
  propertyFilterSchema,
  propertyFormSchema,
} from '@/lib/validation/property';
import { ZodError } from 'zod';

export interface ListPropertiesArgs {
  user: AppUser | null;
  pagination: PaginationInput;
  filters?: PropertyListFilters;
  signal?: AbortSignal;
  sort?: PropertyListSorting;
}

export interface PropertyDetailArgs {
  user: AppUser | null;
  propertyId: string;
}

export interface CreatePropertyArgs {
  user: AppUser | null;
  values: PropertyFormValues;
}

export interface UpdatePropertyArgs {
  user: AppUser | null;
  propertyId: string;
  values: PropertyFormValues;
}

export interface PropertyIdArgs {
  user: AppUser | null;
  propertyId: string;
}

const ANALYTICS_ORIGIN = 'admin_property_service';

export async function listPropertiesForUser({
  user,
  pagination,
  filters,
  signal,
  sort,
}: ListPropertiesArgs): Promise<PropertyListResult> {
  const actor = requireAdminOrManager(user);

  try {
    const safePagination = paginationSchema.parse(pagination);
    const safeFilters = filters ? propertyFilterSchema.parse(filters) : undefined;
    return await listProperties({
      pagination: safePagination,
      filters: safeFilters,
      signal,
      sort,
    });
  } catch (error) {
    handleServiceError(error);
  } finally {
    // actor currently unused but enforces role check
    void actor;
  }
}

export async function getPropertyDetailForUser({
  user,
  propertyId,
}: PropertyDetailArgs): Promise<PropertyDetailResult> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    return await getPropertyById(propertyId);
  } catch (error) {
    handleServiceError(error);
  }
}

export async function createPropertyForUser({
  user,
  values,
}: CreatePropertyArgs): Promise<PropertyDetailResult> {
  const actor = requireAdminOrManager(user);

  try {
    const parsed = propertyFormSchema.parse(values);
    const detail = await createProperty(parsed, actor.id);
    trackPropertyCreateSuccess({
      propertyId: detail.summary.id,
      assetType: detail.summary.assetType,
      submarket: detail.summary.submarket,
      origin: ANALYTICS_ORIGIN,
    });
    return detail;
  } catch (error) {
    const metadata = extractErrorMetadata(error);
    trackPropertyCreateFailure({
      propertyCode: values.propertyCode,
      errorName: metadata.errorName,
      errorMessage: metadata.errorMessage,
      origin: ANALYTICS_ORIGIN,
    });
    handleServiceError(error);
  }
}

export async function updatePropertyForUser({
  user,
  propertyId,
  values,
}: UpdatePropertyArgs): Promise<PropertyDetailResult> {
  const actor = requireAdminOrManager(user);

  try {
    const parsed = propertyFormSchema.parse(values);
    const detail = await updateProperty(propertyId, parsed, actor.id);
    trackPropertyUpdateSuccess({
      propertyId: detail.summary.id,
      origin: ANALYTICS_ORIGIN,
    });
    return detail;
  } catch (error) {
    const metadata = extractErrorMetadata(error);
    trackPropertyUpdateFailure({
      propertyId,
      errorName: metadata.errorName,
      errorMessage: metadata.errorMessage,
      origin: ANALYTICS_ORIGIN,
    });
    handleServiceError(error);
  }
}

export async function deletePropertyForUser({ user, propertyId }: PropertyIdArgs): Promise<void> {
  const actor = requireAdminOrManager(user);
  void actor;

  try {
    await deleteProperty(propertyId);
  } catch (error) {
    handleServiceError(error);
  }
}

function extractErrorMetadata(error: unknown): { errorName: string; errorMessage?: string } {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
    };
  }

  return {
    errorName: 'UnknownError',
    errorMessage: undefined,
  };
}

function handleServiceError(error: unknown): never {
  if (error instanceof ZodError) {
    throw fromZodError(error);
  }

  if (isServiceError(error)) {
    throw error;
  }

  throw normalizeError(error);
}
